import * as SunCalc from "suncalc";
import { eciToEcf, eciToGeodetic, gstime, propagate, SatRec } from "satellite.js";
import { OrbitalPass, OrbitalPassStep } from "../OrbitFeasibilityStudyWebWorkerTypes";
import { LatLngAlt, LngLatAlt } from "./Geography";

export interface SatData { id: string, tle: SatRec };

export const EARTH_RADIUS_KM     = 6371;
export const SIMULATION_TICK_SEC = 1;
export const toRad = ( deg: number ) => deg * Math.PI / 180;
export const toDeg = ( rad: number ) => rad * 180     / Math.PI;

export function getSunDirection( date: Date ): [number, number, number] {
    const sunPos = SunCalc.getPosition( date, 0, 0 );
    const alt    = sunPos.altitude;
    const azi    = sunPos.azimuth;
    const x = Math.cos( alt ) * Math.sin( azi );
    const y = Math.cos( alt ) * Math.cos( azi );
    const z = Math.sin( alt );
    return [x, y, z];
}

export function haversineKm( lat1: number, lon1: number, lat2: number, lon2: number ): number {
    const R   = EARTH_RADIUS_KM;
    const dla = toRad( lat2 - lat1 );
    const dlo = toRad( lon2 - lon1 );
    const a   = Math.sin( dla / 2 ) ** 2 
        + Math.cos( toRad( lat1 ) ) 
        * Math.cos( toRad( lat2 ) ) 
        * Math.sin( dlo / 2 ) ** 2;
    return R * 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );
}

export const sgp4ToLonLatAlt = ( rec: SatRec, date: Date ): LngLatAlt => {
    const pv = propagate( rec, date );
    if( !pv?.position ) {
        console.error( "Error! Investigate!" );
        debugger;
        return [0, 0, 0] as LngLatAlt;
    }
    const geo = eciToGeodetic( pv.position, gstime( date ) );
    const lon = ( geo.longitude * 180 ) / Math.PI;
    const lat = ( geo.latitude  * 180 ) / Math.PI;
    const alt = geo.height;
    return [lon, lat, alt] as LngLatAlt;
};

export const sgp4ToLatLonAlt = ( rec: SatRec, date: Date ): LatLngAlt => {
    const res = sgp4ToLonLatAlt( rec, date );
    return [res[1], res[0], res[2]] as LatLngAlt;
}

export function getSatelliteEcefKm( rec: SatRec, date: Date ): [number, number, number] {
    const pv = propagate( rec, date );
    if( !pv?.position ) {
        console.error( "Error! Investigate!" );
        return [0, 0, 0];
    }
    const gmst = gstime( date );
    const ecf  = eciToEcf( pv.position, gmst );
    return [ecf.x, ecf.y, ecf.z]; //km
}

export type SatelliteVisibility = {
    canSee:        boolean;
    elevation:     number; //degrees
    offNadirAngle: number,
    azimuth:       number; //degrees (0-360, from north)
    range:         number; //km
};

function llaToEcefWgs84( latDeg: number, lonDeg: number, altKm: number ) {
    const a      = 6378.137; //km
    const f      = 1 / 298.257223563;
    const e2     = f * ( 2 - f );
    const lat    = toRad( latDeg );
    const lon    = toRad( lonDeg );
    const sinLat = Math.sin( lat );
    const cosLat = Math.cos( lat );
    const N      = a / Math.sqrt( 1 - e2 * sinLat * sinLat );
    return [
        ( N + altKm ) * cosLat * Math.cos( lon ),
        ( N + altKm ) * cosLat * Math.sin( lon ),
        ( N * ( 1 - e2 ) + altKm ) * sinLat
    ];
}

export function calculateVisibilityEcef(
    satEcef:   [number, number, number], //km
    targetLat: number,                   //deg
    targetLon: number                    //deg
): SatelliteVisibility {

    //ground point in ecef
    const tgtEcef = llaToEcefWgs84( targetLat, targetLon, 0 );

    //line-of-sight vector (target -> satellite)
    const rx    = satEcef[0] - tgtEcef[0];
    const ry    = satEcef[1] - tgtEcef[1];
    const rz    = satEcef[2] - tgtEcef[2];
    const range = Math.sqrt( rx * rx + ry * ry + rz * rz );

    //enu basis at target
    const lat    = toRad( targetLat );
    const lon    = toRad( targetLon );
    const sinLat = Math.sin( lat );
    const cosLat = Math.cos( lat );
    const sinLon = Math.sin( lon );
    const cosLon = Math.cos( lon );

    //east
    const ex = -sinLon;
    const ey = cosLon;
    const ez = 0;

    //north
    const nx = -sinLat * cosLon;
    const ny = -sinLat * sinLon;
    const nz = cosLat;

    //up
    const ux = cosLat * cosLon;
    const uy = cosLat * sinLon;
    const uz = sinLat;

    //project los into enu
    const east  = rx * ex + ry * ey + rz * ez;
    const north = rx * nx + ry * ny + rz * nz;
    const up    = rx * ux + ry * uy + rz * uz;

    //azimuth and elevation
    const azimuth   = ( toDeg( Math.atan2( east, north ) ) + 360 ) % 360;
    const elevation = toDeg( Math.asin( up / range ) );

    //off-nadir angle (from satellite perspective)
    //satellite-to-target vector (opposite of target-to-satellite)
    const stX = -rx;
    const stY = -ry;
    const stZ = -rz;
    
    //nadir vector (satellite to Earth center)
    const nadirX = -satEcef[0];
    const nadirY = -satEcef[1];
    const nadirZ = -satEcef[2];
    const nadirMag = Math.sqrt( nadirX * nadirX + nadirY * nadirY + nadirZ * nadirZ );

    //dot product (range already equals magnitude of st vector)
    const dot = (stX * nadirX + stY * nadirY + stZ * nadirZ) / (range * nadirMag);
    const offNadirAngle = toDeg( Math.acos( Math.max(-1, Math.min(1, dot)) ) );

    const canSee = elevation > 0;    
    return { canSee, elevation, offNadirAngle, azimuth, range };
}

function calculateOrbitalPeriod( altitudeKm: number ): number {    
    const earthRadiusKm = 6371;     //mean earth radius in km
    const mu            = 3.986e14; //earth's gravitational parameter in m^3/s^2

    // convert to meters and calculate orbital radius
    const altitude    = altitudeKm    * 1000;
    const radiusEarth = earthRadiusKm * 1000;
    const radiusOrbit = altitude + radiusEarth;

    // apply kepler's third law: t = 2*pi*sqrt(r^3/mu)
    const periodSeconds = 2 * Math.PI * Math.sqrt(
        Math.pow( radiusOrbit, 3 ) / mu
    );
    return periodSeconds;
}

function partition<T>( arr: Array<T>, func: ( prev: T | undefined, curr: T ) => boolean ) {
    const res: Array<Array<T>> = [];
    let lst: Array<T> = [];
    for( let i = 0; i < arr.length; i++ ) {
        const prev = arr[i - 1]; //apparently this returns the undefined
        const rec = arr[i];
        //rec belongs to current partition
        if( !func( prev, rec ) ) {
            lst.push( rec );
            continue;
        }
        //this rec belongs to a new partition
        if( lst.length !== 0 ) {
            res.push( lst );
            lst = [];
        }
        lst.push( rec );
    }
    if( lst.length !== 0 ) {
        res.push( lst );
    }
    return res;
}

export function computePasses( orbitSteps: OrbitalPassStep[] ) {
    const results: OrbitalPass[] = [];
    const passes = partition( orbitSteps, ( prev, curr ) => {
        if( prev === undefined ) {
            return false;
        }
        if( Math.abs( curr.tick - prev.tick ) !== ( SIMULATION_TICK_SEC * 1000 ) ) {
            return true;
        }
        return false;
    } );
    for( let passInd = 0; passInd < passes.length; passInd++ ) {
        const pass = passes[passInd];
        const n = pass.length;
        let currInd = -1;
        for( let i = 0; i < n; i++ ) {
            if( i ===     0 ) { continue; }
            if( i === n - 1 ) { continue; }
            const prev = pass[i - 1];
            const curr = pass[i + 0];
            const next = pass[i + 1];
            if( curr.slantRange < prev.slantRange && curr.slantRange < next.slantRange ) {
                currInd = i;
                break;
            }
        }
        if( currInd === -1 ) {
            console.log( "Warning: pass did not have an apex" )
            continue;
        }
        const curr = pass[currInd];
        const init: OrbitalPassStep = pass[0];
        const last: OrbitalPassStep = pass[n - 1];

        const APEX_WINDOW = 1;
        const winStart = Math.max( 0,     currInd - APEX_WINDOW );
        const winEnd   = Math.min( n - 1, currInd + APEX_WINDOW );
        const win      = pass.slice( winStart, winEnd + 1 );
        const alts     = win.map( s => s.altitude );
        const onas     = win.map( s => s.offNadir );
        results.push( {
            satelliteName: curr.satelliteName,
            date:          curr.date,
            earlyTime:     init.tick,
            midTime:       curr.tick,
            lastTime:      last.tick,
            samples:       pass.length,
            firstAlt:      Math.min( ...alts ),
            apexAlt:       curr.altitude,
            lastAlt:       Math.max( ...alts ),
            sunElev:       curr.sunElev,
            firstOffNadir: Math.min( ...onas ),
            apexOffNadir:  curr.offNadir,
            lastOffNadir:  Math.max( ...onas ),
            cloudCover:    curr.cloudCover,
        } );
    }
    return results;
}